/**
 * Column mapping and table configuration for the IC Settlements table.
 * Based on the SettlementOutput shape from LiquidityEngineDataModel.
 *
 * SETTLEMENT_FIELDS is the complete set of fields from the API, in the same order as the Smithy model.
 * TABLE_COLUMNS selects a subset of fields to display in the table and controls default visibility.
 * Both are frozen to prevent accidental runtime modification.
 */

/** Creates a frozen field descriptor for a SettlementOutput field. */
const defineField = (key, label, type, sortable) => Object.freeze({ key, label, type, sortable });

/**
 * All fields from SettlementOutput with their key, display label, Smithy type, and whether they are sortable.
 */
export const SETTLEMENT_FIELDS = Object.freeze({
    settlementId:           defineField('settlementId',           'Settlement ID',            'String',                  true),
    uploadFile:             defineField('uploadFile',             'Upload File',              'String',                  true),
    ocmMapping:             defineField('ocmMapping',             'OCM Mapping',              'String',                  true),
    status:                 defineField('status',                 'Status',                   'ICSettlementStatus',      true),
    workflowExecutionId:    defineField('workflowExecutionId',    'Workflow Execution ID',    'String',                  true),
    fileType:               defineField('fileType',               'File Type',                'String',                  true),
    settlementCycle:        defineField('settlementCycle',        'Settlement Cycle',         'String',                  true),
    settlementMethod:       defineField('settlementMethod',       'Settlement Method',        'String',                  true),
    natureOfBalance:        defineField('natureOfBalance',        'Nature of Balance',        'String',                  true),
    paymentType:            defineField('paymentType',            'Payment Type',             'String',                  true),
    wirePath:               defineField('wirePath',               'Wire Path',                'String',                  true),
    wireStatus:             defineField('wireStatus',             'Wire Status',              'String',                  true),
    reasonCode:             defineField('reasonCode',             'Reason Code',              'String',                  true),
    settlementHistory:      defineField('settlementHistory',      'Settlement History',       'String',                  true),
    maximumSettleDate:      defineField('maximumSettleDate',      'Maximum Settle Date',      'String',                  true),
    complianceCheck:        defineField('complianceCheck',        'Compliance Check',         'String',                  true),
    approvalAttribute:      defineField('approvalAttribute',      'Approval Attribute',       'String',                  true),
    rtsSignal:              defineField('rtsSignal',              'RTS Signal',               'String',                  true),
    frequency:              defineField('frequency',              'Frequency',                'String',                  true),
    sendingCo:              defineField('sendingCo',              'Sending Co',               'String',                  true),
    sendingCoName:          defineField('sendingCoName',          'Sending Co Name',          'String',                  true),
    sendingAccount:         defineField('sendingAccount',         'Sending Account',          'String',                  true),
    sendingCurrency:        defineField('sendingCurrency',        'Sending Currency',         'String',                  true),
    sendingAmount:          defineField('sendingAmount',          'Sending Amount',           'String',                  true),
    region:                 defineField('region',                 'Region',                   'String',                  true),
    pobo:                   defineField('pobo',                   'POBO',                     'String',                  true),
    receivingCo:            defineField('receivingCo',            'Receiving Co',             'String',                  true),
    receivingCoName:        defineField('receivingCoName',        'Receiving Co Name',        'String',                  true),
    receivingAccount:       defineField('receivingAccount',       'Receiving Account',        'String',                  true),
    receivingCurrency:      defineField('receivingCurrency',      'Receiving Currency',       'String',                  true),
    receivingAmount:        defineField('receivingAmount',        'Receiving Amount',         'String',                  true),
    receivingRegion:        defineField('receivingRegion',        'Receiving Region',         'String',                  true),
    robo:                   defineField('robo',                   'ROBO',                     'String',                  true),
    targetAmount:           defineField('targetAmount',           'Target Amount',            'String',                  true),
    targetCurrency:         defineField('targetCurrency',         'Target Currency',          'String',                  true),
    settlementUsdEquiv:     defineField('settlementUsdEquiv',     'Settlement USD Equiv',     'String',                  true),
    valueDate:              defineField('valueDate',              'Value Date',               'String',                  true),
    createdAt:              defineField('createdAt',              'Created At',               'Timestamp',               true),
    updatedAt:              defineField('updatedAt',              'Updated At',               'Timestamp',               true),
    liquidityCheckResult:   defineField('liquidityCheckResult',   'Liquidity Check Result',   'StringMap',               false),
    liquidityApprovedBy:    defineField('liquidityApprovedBy',    'Liquidity Approved By',    'String',                  true),
    liquidityApprovedDate:  defineField('liquidityApprovedDate',  'Liquidity Approved Date',  'Timestamp',               true),
    rejectedBy:             defineField('rejectedBy',             'Rejected By',              'String',                  true),
    rejectionDate:          defineField('rejectionDate',          'Rejection Date',           'Timestamp',               true),
    rejectionReason:        defineField('rejectionReason',        'Rejection Reason',         'String',                  true),
    exportDestination:      defineField('exportDestination',      'Export Destination',       'String',                  true),
    exportReference:        defineField('exportReference',        'Export Reference',         'String',                  true),
    exportedBy:             defineField('exportedBy',             'Exported By',              'String',                  true),
    exportDate:             defineField('exportDate',             'Export Date',              'Timestamp',               true),
    uploadUser:             defineField('uploadUser',             'Upload User',              'String',                  true),
    createdBy:              defineField('createdBy',              'Created By',               'String',                  true),
    reviewer:               defineField('reviewer',               'Reviewer',                 'String',                  true),
    updateBy:               defineField('updateBy',               'Update By',                'String',                  true),
    specialWireInstruction: defineField('specialWireInstruction', 'Special Wire Instruction', 'String',                  true),
    userComments:           defineField('userComments',           'User Comments',            'String',                  true),
    attachments:            defineField('attachments',            'Attachments',              'AttachmentMetadataList',  false),
    errorDetails:           defineField('errorDetails',           'Error Details',            'StringMap',               false),
    version:                defineField('version',                'Version',                  'Long',                    true),
    redriveCount:           defineField('redriveCount',           'Redrive Count',            'Integer',                 true),
    lastRedrivedAt:         defineField('lastRedrivedAt',         'Last Redrived At',         'Timestamp',               true),
    lastRedrivedBy:         defineField('lastRedrivedBy',         'Last Redrived By',         'String',                  true),
    notifiedAt:             defineField('notifiedAt',             'Notified At',              'Timestamp',               true),
    notifiedBy:             defineField('notifiedBy',             'Notified By',              'String',                  true),
    rerunLiquidityCheckAt:  defineField('rerunLiquidityCheckAt',  'Rerun Liquidity Check At', 'Timestamp',               true),
    rerunLiquidityCheckBy:  defineField('rerunLiquidityCheckBy',  'Rerun Liquidity Check By', 'String',                  true),
    exportedAt:             defineField('exportedAt',             'Exported At',              'Timestamp',               true),
});

/**
 * Table columns configuration. Subset of SETTLEMENT_FIELDS that maps to columns displayed
 * in the existing TRS IC settlements UI. Order here determines column order in the table.
 * Hidden columns are available via table preferences but not shown by default.
 */
export const TABLE_COLUMNS = Object.freeze([
    { ref: SETTLEMENT_FIELDS.settlementId,           hidden: false },
    { ref: SETTLEMENT_FIELDS.status,                 hidden: false },
    { ref: SETTLEMENT_FIELDS.sendingCo,              hidden: false },
    { ref: SETTLEMENT_FIELDS.sendingCoName,          hidden: false },
    { ref: SETTLEMENT_FIELDS.sendingCurrency,        hidden: false },
    { ref: SETTLEMENT_FIELDS.sendingAccount,         hidden: false },
    { ref: SETTLEMENT_FIELDS.sendingAmount,          hidden: false },
    { ref: SETTLEMENT_FIELDS.receivingCo,            hidden: false },
    { ref: SETTLEMENT_FIELDS.receivingCoName,        hidden: false },
    { ref: SETTLEMENT_FIELDS.receivingCurrency,      hidden: false },
    { ref: SETTLEMENT_FIELDS.receivingAccount,       hidden: false },
    { ref: SETTLEMENT_FIELDS.receivingAmount,        hidden: false },
    { ref: SETTLEMENT_FIELDS.targetAmount,           hidden: false },
    { ref: SETTLEMENT_FIELDS.targetCurrency,         hidden: false },
    { ref: SETTLEMENT_FIELDS.settlementUsdEquiv,     hidden: false },
    { ref: SETTLEMENT_FIELDS.valueDate,              hidden: false },
    { ref: SETTLEMENT_FIELDS.region,                 hidden: false },
    { ref: SETTLEMENT_FIELDS.receivingRegion,        hidden: false },
    { ref: SETTLEMENT_FIELDS.paymentType,            hidden: false },
    { ref: SETTLEMENT_FIELDS.fileType,               hidden: false },
    { ref: SETTLEMENT_FIELDS.settlementCycle,        hidden: false },
    { ref: SETTLEMENT_FIELDS.natureOfBalance,        hidden: false },
    { ref: SETTLEMENT_FIELDS.specialWireInstruction, hidden: false },
    { ref: SETTLEMENT_FIELDS.liquidityCheckResult,   hidden: false },
    { ref: SETTLEMENT_FIELDS.complianceCheck,        hidden: false },
    { ref: SETTLEMENT_FIELDS.frequency,              hidden: false },
    { ref: SETTLEMENT_FIELDS.ocmMapping,             hidden: true },
    { ref: SETTLEMENT_FIELDS.uploadFile,             hidden: true },
    { ref: SETTLEMENT_FIELDS.uploadUser,             hidden: true },
    { ref: SETTLEMENT_FIELDS.createdBy,              hidden: true },
    { ref: SETTLEMENT_FIELDS.createdAt,              hidden: true },
    { ref: SETTLEMENT_FIELDS.updateBy,               hidden: true },
    { ref: SETTLEMENT_FIELDS.updatedAt,              hidden: true },
    { ref: SETTLEMENT_FIELDS.liquidityApprovedBy,    hidden: true },
    { ref: SETTLEMENT_FIELDS.liquidityApprovedDate,  hidden: true },
    { ref: SETTLEMENT_FIELDS.rejectedBy,             hidden: true },
    { ref: SETTLEMENT_FIELDS.rejectionDate,          hidden: true },
    { ref: SETTLEMENT_FIELDS.exportedBy,             hidden: true },
    { ref: SETTLEMENT_FIELDS.exportDate,             hidden: true },
].map(Object.freeze));

const PRIMITIVE_TYPES = new Set(['String', 'Timestamp', 'Long', 'Integer', 'ICSettlementStatus']);

/**
 * Builds the column definitions array consumed by the Cloudscape Table component.
 * Each entry defines the column id, header label, cell renderer, and sorting field.
 * Only columns marked as sortable in SETTLEMENT_FIELDS will have a sortingField set.
 * Non-primitive types are rendered as JSON strings to avoid [object Object].
 */
export function buildColumnDefinitions() {
    return TABLE_COLUMNS.map(({ ref }) => ({
        id: ref.key,
        header: ref.label,
        cell: PRIMITIVE_TYPES.has(ref.type)
            ? item => item[ref.key]
            : item => JSON.stringify(item[ref.key]),
        ...(ref.sortable && { sortingField: ref.key }),
    }));
}

/**
 * Builds the content selector options for the Cloudscape CollectionPreferences component.
 * Controls which columns are visible by default and which can be toggled by the user.
 */
export function buildContentSelectorOptions() {
    return [{
        label: 'Properties',
        options: TABLE_COLUMNS.map(({ ref, hidden }) => ({
            id: ref.key,
            label: ref.label,
            editable: true,
            visible: !hidden,
        })),
    }];
}

/**
 * Builds the filter options for the Cloudscape PropertyFilter component.
 * Allows users to filter table rows by any column value.
 * Non-primitive types are excluded since filtering on objects is not meaningful.
 */
export function buildFilterOptions() {
    return TABLE_COLUMNS
        .filter(({ ref }) => PRIMITIVE_TYPES.has(ref.type))
        .map(({ ref }) => ({
            propertyLabel: ref.label,
            propertyKey: ref.key,
            groupValuesLabel: ref.label + ' values',
            values: [],
        }));
}
